import type { SagaIterator } from 'redux-saga';
import { call, put, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import { getLogger, httpClientInterface, requireEnvironmentConfig } from '../../utils/core';
import { categoriesActions } from '../categories';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { QcsSyncResourcesConfig } from '../core/resourceStoreTypes';
import { filtersActions, filtersTypes } from '../filters';
import { tagsActions } from '../tags';
import { transactionsActions } from '../transactions';
import * as actions from './spendingWatchlistActions';
import { getLastSyncDate } from './spendingWatchlistSelectors';
import {
  transformResponseToSpendingWatchLists,
  transformSpendingWatchListToRequestData,
} from './spendingWatchlistTransformers';
import { mkSpendingWatchList, type SpendingWatchlistProps } from './spendingWatchlistTypes';

const log = getLogger('data/spendingWatchList/sagas');

export function* createWatchListWorker(action: ReturnType<typeof actions.createWatchList>): SagaIterator {
  const title = action.payload.get('title');
  const targetAmt = action.payload.get('targetAmount');
  const emoji = action.payload.get('emoji');
  const period = action.payload.get('period');
  const startDate = action.payload.get('startDate');
  const endDate = action.payload.get('endDate');

  const filterToCreate = action.payload.get('filterItems');
  const filterRequestId = filterToCreate?.clientId || uuidv4().toUpperCase();
  const filterRequestContext = `spending-watchlist:${filterRequestId}`;

  yield put(filtersActions.createFilter(filterToCreate, { context: filterRequestContext }));

  const result: ReturnType<typeof filtersActions.createFilterSuccess | typeof filtersActions.createFilterFailure> =
    yield take(
      (filterAction: { type: string }) =>
        (filtersActions.createFilterSuccess.match(filterAction) ||
          filtersActions.createFilterFailure.match(filterAction)) &&
        filterAction.meta?.context === filterRequestContext,
    );
  const filterId = filtersActions.createFilterSuccess.match(result) ? result.payload?.id : undefined;

  // Without a filter the watchlist is unusable: the server rejects it and the optimistic record can
  // never be reconciled, leaving a row in the list that no selector can resolve. Stop here instead.
  if (!filterId) {
    yield put(actions.createWatchListFailure(result.payload));
    return;
  }

  // Attached returned filter Id to spending watch list
  const newWatchList = mkSpendingWatchList({
    id: null,
    clientId: uuidv4().toUpperCase(),
    name: title,
    isDeleted: null,
    filterId,
    targetAmount: targetAmt,
    emoji,
    period,
    startDate,
    endDate,
  });

  yield put(actions.createSpendingWatchList(newWatchList));
}

function* deleteWatchListWorker(action: ReturnType<typeof actions.deleteWatchList>): SagaIterator {
  const { payload: resource } = action;

  if (resource.filterToDelete) {
    yield put(filtersActions.deleteFilter(filtersTypes.mkFilter(resource.filterToDelete)));
  }
  yield put(actions.deleteSpendingWatchList(resource.spendingWatchListToDelete));
}
// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

export function* createWatchListActionWatcher(): SagaIterator {
  yield takeLatest(actions.createWatchList.type, createWatchListWorker);
}

export function* deleteWatchListActionWatcher(): SagaIterator {
  yield takeEvery(actions.deleteWatchList.type, deleteWatchListWorker);
}

const createResourceConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<SpendingWatchlistProps>();
type WatchlistSuccessActionCreator =
  | typeof actions.getSpendingWatchListsSuccess
  | typeof actions.createSpendingWatchListSuccess
  | typeof actions.updateSpendingWatchListSuccess
  | typeof actions.deleteSpendingWatchListSuccess;
type WatchlistFailureActionCreator =
  | typeof actions.getSpendingWatchListsFailure
  | typeof actions.createSpendingWatchListFailure
  | typeof actions.updateSpendingWatchListFailure
  | typeof actions.deleteSpendingWatchListFailure;

const mkResourceConfig = (
  successAction: WatchlistSuccessActionCreator,
  failureAction: WatchlistFailureActionCreator,
): QcsSyncResourcesConfig<SpendingWatchlistProps> =>
  createResourceConfig({
    resourceName: 'spendingWatchList',
    resourceBaseUrl: '/spending-watchlist',
    getLastSyncDate,

    transformResponseToResources: transformResponseToSpendingWatchLists,
    transformResourceToRequestData: transformSpendingWatchListToRequestData,

    successAction,
    failureAction,
  });

function* getSpendingWatchlistNoCalc(action: ReturnType<typeof actions.getSpendingWatchListsInitial>): SagaIterator {
  yield call(
    resourceSagaUtils.qcsSyncGetResources<SpendingWatchlistProps>,
    mkResourceConfig(actions.getSpendingWatchListsSuccess, actions.getSpendingWatchListsFailure),
    action,
  );
}

function* getSpendingWatchListCalc(action: ReturnType<typeof actions.getSpendingWatchLists>): SagaIterator {
  try {
    log.log('getSpendingWatchListAction: server calculation start...');

    const response: unknown = yield call(
      httpClientInterface.send,
      'GET',
      `${requireEnvironmentConfig().services_url}/spending-watchlist/calc`,
    );

    log.log('getSpendingWatchListAction: server calculation complete...\nresponse = ', response);
  } catch (error) {
    log.log('getSpendingWatchListAction: server calculation failed...\nerror = ', error);
  }

  yield call(
    resourceSagaUtils.qcsSyncGetResources<SpendingWatchlistProps>,
    mkResourceConfig(actions.getSpendingWatchListsSuccess, actions.getSpendingWatchListsFailure),
    action,
  );
}

export function* getSpendingWatchListInitialActionWatcher(): SagaIterator {
  yield takeLeading(actions.getSpendingWatchListsInitial.type, getSpendingWatchlistNoCalc);
}

export function* getSpendingWatchListActionWatcher(): SagaIterator {
  yield takeLeading(actions.getSpendingWatchLists.type, getSpendingWatchListCalc);
}

export function* createSpendingWatchListActionWatcher(): SagaIterator {
  const resourceConfig = mkResourceConfig(
    actions.createSpendingWatchListSuccess,
    actions.createSpendingWatchListFailure,
  );
  yield takeEvery(
    actions.createSpendingWatchList.type,
    resourceSagaUtils.qcsSyncCreateResource<SpendingWatchlistProps>,
    resourceConfig,
  );
}

export function* updateSpendingWatchListActionWatcher(): SagaIterator {
  const resourceConfig = mkResourceConfig(
    actions.updateSpendingWatchListSuccess,
    actions.updateSpendingWatchListFailure,
  );
  yield takeEvery(
    actions.updateSpendingWatchList.type,
    resourceSagaUtils.qcsSyncUpdateResource<SpendingWatchlistProps>,
    resourceConfig,
  );
}

export function* deleteSpendingWatchListActionWatcher(): SagaIterator {
  const resourceConfig = mkResourceConfig(
    actions.deleteSpendingWatchListSuccess,
    actions.deleteSpendingWatchListFailure,
  );
  yield takeEvery(
    actions.deleteSpendingWatchList.type,
    resourceSagaUtils.qcsSyncDeleteResource<SpendingWatchlistProps>,
    resourceConfig,
  );
}

function* spendingWatchListCalc(): SagaIterator {
  yield put(filtersActions.getFilters());
  yield put(actions.getSpendingWatchLists());
}

function* spendingWatchListCalcTriggersWatcher(): SagaIterator {
  yield takeEvery(
    [
      actions.createSpendingWatchListSuccess.type,
      actions.updateSpendingWatchListSuccess.type,
      actions.deleteSpendingWatchListSuccess.type,
      transactionsActions.createTransactionSuccess,
      transactionsActions.batchTransactionsSuccess,
      transactionsActions.updateTransactionSuccess,
      transactionsActions.deleteTransactionSuccess,
      transactionsActions.performTransactionActionSuccess,
      categoriesActions.createCategorySuccess,
      tagsActions.deleteTag,
    ],
    function* spendingWatchListCalcHandler(action: { type: string; meta?: { context?: string } }): SagaIterator {
      let doCalc;
      switch (action.type) {
        case String(transactionsActions.createTransactionSuccess):
        case String(transactionsActions.batchTransactionsSuccess):
        case String(transactionsActions.updateTransactionSuccess):
        case String(transactionsActions.deleteTransactionSuccess):
          if (action.meta?.context === 'Reg_SpendingWatchListGraph') {
            doCalc = true;
          }
          break;
        default:
          doCalc = true;
      }
      if (doCalc) {
        yield call(spendingWatchListCalc);
      }
    },
  );
}

export default [
  createWatchListActionWatcher,
  deleteWatchListActionWatcher,
  getSpendingWatchListInitialActionWatcher,
  getSpendingWatchListActionWatcher,
  createSpendingWatchListActionWatcher,
  updateSpendingWatchListActionWatcher,
  deleteSpendingWatchListActionWatcher,
  spendingWatchListCalcTriggersWatcher,
];
